import { ascMC, obliquity, normalize, DEG, RAD } from './ephemeris';

// ---------------------------------------------------------------------------
// Spherical helpers
// ---------------------------------------------------------------------------

type Vec3 = [number, number, number];

/** Right ascension (deg) of an ecliptic longitude (latitude 0). */
function raOf(lonDeg: number, eps: number): number {
  return normalize(Math.atan2(Math.sin(lonDeg * DEG) * Math.cos(eps), Math.cos(lonDeg * DEG)) * RAD);
}

/** Declination (rad) of an ecliptic longitude (latitude 0). */
function decOf(lonDeg: number, eps: number): number {
  return Math.asin(Math.sin(lonDeg * DEG) * Math.sin(eps));
}

function vec(raDeg: number, decRad: number): Vec3 {
  const r = raDeg * DEG;
  const c = Math.cos(decRad);
  return [c * Math.cos(r), c * Math.sin(r), Math.sin(decRad)];
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

/** Ecliptic longitude of an equatorial unit vector. */
function eclLonOf(v: Vec3, eps: number): number {
  const x = v[0];
  const y = v[1] * Math.cos(eps) + v[2] * Math.sin(eps);
  if (x === 0 && y === 0) return 0;
  return normalize(Math.atan2(y, x) * RAD);
}

/** Pick λ or λ+180 — whichever is closest to the expected (Porphyry) cusp. */
function nearest(lambda: number, expected: number): number {
  const d = (a: number, b: number) => {
    let x = Math.abs(normalize(a - b));
    if (x > 180) x = 360 - x;
    return x;
  };
  const alt = normalize(lambda + 180);
  return d(lambda, expected) <= d(alt, expected) ? normalize(lambda) : alt;
}

/**
 * The classic "ascendant under a pole" formula. With `ramcEff = RAMC` and
 * `tanPole = tan(φ)` this returns the Ascendant itself; other combinations
 * generate Koch and Topocentric intermediate cusps.
 */
function ascUnderPole(ramcEff: number, tanPole: number, eps: number): number {
  const r = ramcEff * DEG;
  return normalize(
    Math.atan2(-Math.cos(r), Math.sin(r) * Math.cos(eps) + tanPole * Math.sin(eps)) * RAD,
  );
}

// ---------------------------------------------------------------------------
// Porphyry (also the universal fallback)
// ---------------------------------------------------------------------------

/** Porphyry: equal trisection of the four quadrants ASC/MC/DSC/IC. */
function porphyryFrom(asc: number, mc: number): number[] {
  const dsc = normalize(asc + 180);
  const ic = normalize(mc + 180);
  const cusps = new Array(13);
  cusps[1] = asc;
  cusps[10] = mc;
  cusps[4] = ic;
  cusps[7] = dsc;
  const arcMcAsc = normalize(asc - mc);
  cusps[11] = normalize(mc + arcMcAsc / 3);
  cusps[12] = normalize(mc + (2 * arcMcAsc) / 3);
  const arcAscIc = normalize(ic - asc);
  cusps[2] = normalize(asc + arcAscIc / 3);
  cusps[3] = normalize(asc + (2 * arcAscIc) / 3);
  cusps[5] = normalize(cusps[11] + 180);
  cusps[6] = normalize(cusps[12] + 180);
  cusps[8] = normalize(cusps[2] + 180);
  cusps[9] = normalize(cusps[3] + 180);
  return cusps;
}

/** Assemble a full 12-cusp array from the four angles + cusps 11, 12, 2, 3. */
function assemble(asc: number, mc: number, c11: number, c12: number, c2: number, c3: number): number[] {
  const cusps = new Array(13);
  cusps[1] = normalize(asc);
  cusps[10] = normalize(mc);
  cusps[4] = normalize(mc + 180);
  cusps[7] = normalize(asc + 180);
  cusps[11] = normalize(c11);
  cusps[12] = normalize(c12);
  cusps[2] = normalize(c2);
  cusps[3] = normalize(c3);
  cusps[5] = normalize(c11 + 180);
  cusps[6] = normalize(c12 + 180);
  cusps[8] = normalize(c2 + 180);
  cusps[9] = normalize(c3 + 180);
  return cusps;
}

/**
 * Cusps must advance monotonically in Ascendant-rotated space and no house may
 * be degenerate or absurdly wide. Guards every quadrant system against a
 * non-convergent solver silently producing nonsense house numbers.
 */
export function validateCusps(cusps: number[]): boolean {
  if (!Array.isArray(cusps) || cusps.length < 13) return false;
  const asc = cusps[1];
  if (typeof asc !== 'number' || !Number.isFinite(asc)) return false;
  let prev = 0;
  for (let h = 2; h <= 12; h++) {
    const v = cusps[h];
    if (typeof v !== 'number' || !Number.isFinite(v)) return false;
    const arc = normalize(v - asc);
    if (arc <= prev + 1e-9) return false;
    const width = arc - prev;
    if (width < 1 || width > 120) return false;
    prev = arc;
  }
  if (360 - prev < 1 || 360 - prev > 120) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Placidus — semi-arc trisection solved by bisection
// ---------------------------------------------------------------------------

/**
 * Placidus intermediate cusps via the standard semi-arc method.
 * Cusps 11 and 12 sit 1/3 and 2/3 of their own semi-diurnal arc east of the
 * MC; cusps 2 and 3 sit 2/3 and 1/3 of their own semi-nocturnal arc east of
 * the IC. The residual is monotonic across each quadrant, so we bracket it
 * between the known angles and bisect — no damping, no divergence.
 *
 * Reference cusps (Stockton, 1986-01-08, 12:00 local, lat 37.96, lon -121.29):
 *   I   ASC 27°55' Leo, II 21°27' Vir, III 19°52' Lib, IV 22°47' Sco,
 *   V   27°13' Sag,    VI 29°24' Cap,  VII 27°55' Aqu, VIII 21°27' Pis,
 *   IX  19°52' Ari,    X  MC 22°47' Tau, XI 27°13' Gem, XII 29°24' Can.
 */
function placidusCusps(JD: number, latitude: number, longitude: number): number[] {
  const { asc, mc, lst: ramc } = ascMC(JD, latitude, longitude);
  const eps = obliquity(JD) * DEG;
  const phi = latitude * DEG;
  if (Math.abs(latitude) > 66) return porphyryFrom(asc, mc);

  let failed = false;
  // Hour angle in (-360, 0]: 0 at the MC, decreasing eastwards to -180 at the IC.
  const haOf = (lon: number) => -normalize(raOf(lon, eps) - ramc);
  // Semi-diurnal arc (degrees) of the ecliptic point at `lon`.
  const semiDiurnal = (lon: number): number => {
    const cosH = -Math.tan(decOf(lon, eps)) * Math.tan(phi);
    if (Math.abs(cosH) >= 1) { failed = true; return 90; }
    return Math.acos(cosH) * RAD;
  };

  const arcMcAsc = normalize(asc - mc);
  const arcAscIc = normalize(normalize(mc + 180) - asc);

  const solve = (side: 'above' | 'below', F: number): number => {
    const lonAt = (t: number) => side === 'above'
      ? normalize(mc + t * arcMcAsc)
      : normalize(asc + t * arcAscIc);
    const residual = (t: number): number => {
      const lon = lonAt(t);
      const SD = semiDiurnal(lon);
      return side === 'above'
        ? haOf(lon) + F * SD
        : haOf(lon) + 180 - (1 - F) * (180 - SD);
    };
    let lo = 0, hi = 1;
    const fLo = residual(lo);
    if (!(fLo > 0) || !(residual(hi) < 0)) { failed = true; return lonAt(F); }
    for (let i = 0; i < 80; i++) {
      const mid = (lo + hi) / 2;
      if (residual(mid) > 0) lo = mid; else hi = mid;
    }
    return lonAt((lo + hi) / 2);
  };

  const c11 = solve('above', 1 / 3);
  const c12 = solve('above', 2 / 3);
  const c2 = solve('below', 1 / 3);
  const c3 = solve('below', 2 / 3);
  if (failed) return porphyryFrom(asc, mc);
  const cusps = assemble(asc, mc, c11, c12, c2, c3);
  return validateCusps(cusps) ? cusps : porphyryFrom(asc, mc);
}

// ---------------------------------------------------------------------------
// Sign-based / equal systems
// ---------------------------------------------------------------------------

function wholeSignCusps(JD: number, latitude: number, longitude: number): number[] {
  const { asc } = ascMC(JD, latitude, longitude);
  const ascSign = Math.floor(asc / 30);
  const cusps = new Array(13);
  for (let h = 1; h <= 12; h++) cusps[h] = normalize((ascSign + (h - 1)) * 30);
  return cusps;
}

/** Equal houses: each cusp is 30° from the Ascendant. */
function equalCusps(JD: number, latitude: number, longitude: number): number[] {
  const { asc } = ascMC(JD, latitude, longitude);
  const cusps = new Array(13);
  for (let h = 1; h <= 12; h++) cusps[h] = normalize(asc + (h - 1) * 30);
  return cusps;
}

function porphyryCusps(JD: number, latitude: number, longitude: number): number[] {
  const { asc, mc } = ascMC(JD, latitude, longitude);
  return porphyryFrom(asc, mc);
}

// ---------------------------------------------------------------------------
// Koch (birthplace houses)
// ---------------------------------------------------------------------------

/**
 * Koch: the intermediate cusps are the degrees that rose (or will rise) at
 * one-third steps of the MC's semi-diurnal / semi-nocturnal arc.
 */
function kochCusps(JD: number, latitude: number, longitude: number): number[] {
  const { asc, mc, lst: ramc } = ascMC(JD, latitude, longitude);
  const eps = obliquity(JD) * DEG;
  const phi = latitude * DEG;
  if (Math.abs(latitude) > 66) return porphyryFrom(asc, mc);

  const sinAD = Math.tan(phi) * Math.tan(decOf(mc, eps));
  if (Math.abs(sinAD) >= 1) return porphyryFrom(asc, mc);
  const AD = Math.asin(sinAD) * RAD;
  const SD = 90 + AD; // semi-diurnal arc of the MC degree
  const tanPhi = Math.tan(phi);
  const ref = porphyryFrom(asc, mc);

  const c11 = nearest(ascUnderPole(ramc - (2 * SD) / 3, tanPhi, eps), ref[11]);
  const c12 = nearest(ascUnderPole(ramc - SD / 3, tanPhi, eps), ref[12]);
  const c2 = nearest(ascUnderPole(ramc + SD / 3, tanPhi, eps), ref[2]);
  const c3 = nearest(ascUnderPole(ramc + (2 * SD) / 3, tanPhi, eps), ref[3]);

  const cusps = assemble(asc, mc, c11, c12, c2, c3);
  return validateCusps(cusps) ? cusps : ref;
}

// ---------------------------------------------------------------------------
// Regiomontanus & Campanus — exact great-circle intersections
// ---------------------------------------------------------------------------

/**
 * Both systems place a "position circle" through the north and south points of
 * the horizon and a division point (on the celestial equator for
 * Regiomontanus, on the prime vertical for Campanus). The cusp is where that
 * circle meets the ecliptic, computed here as an exact plane intersection.
 */
function positionCircleCusps(
  kind: 'regiomontanus' | 'campanus',
  JD: number,
  latitude: number,
  longitude: number,
): number[] {
  const { asc, mc, lst: ramc } = ascMC(JD, latitude, longitude);
  const eps = obliquity(JD) * DEG;
  const phi = latitude * DEG;
  if (Math.abs(latitude) > 66) return porphyryFrom(asc, mc);
  const ref = porphyryFrom(asc, mc);

  // North point of the horizon and the prime-vertical basis (east point, zenith).
  const north: Vec3 = [
    -Math.sin(phi) * Math.cos(ramc * DEG),
    -Math.sin(phi) * Math.sin(ramc * DEG),
    Math.cos(phi),
  ];
  const east: Vec3 = [-Math.sin(ramc * DEG), Math.cos(ramc * DEG), 0];
  const zenith: Vec3 = vec(ramc, phi);
  const eclPole: Vec3 = [0, -Math.sin(eps), Math.cos(eps)];

  const cuspAt = (raOffset: number, pvAngle: number, expected: number): number => {
    let division: Vec3;
    if (kind === 'regiomontanus') {
      division = vec(normalize(ramc + raOffset), 0);
    } else {
      const t = pvAngle * DEG;
      const c = Math.cos(t), s = Math.sin(t);
      division = [
        east[0] * c + zenith[0] * s,
        east[1] * c + zenith[1] * s,
        east[2] * c + zenith[2] * s,
      ];
    }
    const normal = cross(north, division);
    const line = cross(normal, eclPole);
    const mag = Math.hypot(line[0], line[1], line[2]);
    if (!Number.isFinite(mag) || mag < 1e-12) return expected;
    const unit: Vec3 = [line[0] / mag, line[1] / mag, line[2] / mag];
    return nearest(eclLonOf(unit, eps), expected);
  };

  // Cusp 11 → 30° of RA past the MC (prime-vertical 60° from the east point).
  const c11 = cuspAt(30, 60, ref[11]);
  const c12 = cuspAt(60, 30, ref[12]);
  const c2 = cuspAt(120, -30, ref[2]);
  const c3 = cuspAt(150, -60, ref[3]);

  const cusps = assemble(asc, mc, c11, c12, c2, c3);
  return validateCusps(cusps) ? cusps : ref;
}

// ---------------------------------------------------------------------------
// Topocentric (Polich–Page)
// ---------------------------------------------------------------------------

function topocentricCusps(JD: number, latitude: number, longitude: number): number[] {
  const { asc, mc, lst: ramc } = ascMC(JD, latitude, longitude);
  const eps = obliquity(JD) * DEG;
  const tanPhi = Math.tan(latitude * DEG);
  if (Math.abs(latitude) > 66) return porphyryFrom(asc, mc);
  const ref = porphyryFrom(asc, mc);

  // Polich–Page: each intermediate cusp is an ascendant taken under a reduced
  // pole (tan P = tan φ · 1/3 or 2/3) at a 30° step of right ascension.
  const c11 = nearest(ascUnderPole(ramc - 60, tanPhi / 3, eps), ref[11]);
  const c12 = nearest(ascUnderPole(ramc - 30, (2 * tanPhi) / 3, eps), ref[12]);
  const c2 = nearest(ascUnderPole(ramc + 30, (2 * tanPhi) / 3, eps), ref[2]);
  const c3 = nearest(ascUnderPole(ramc + 60, tanPhi / 3, eps), ref[3]);

  const cusps = assemble(asc, mc, c11, c12, c2, c3);
  return validateCusps(cusps) ? cusps : ref;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const HOUSE_SYSTEMS = [
  { value: 'placidus', label: 'Placidus' },
  { value: 'whole', label: 'Whole Sign' },
  { value: 'equal', label: 'Equal' },
  { value: 'porphyry', label: 'Porphyry' },
  { value: 'koch', label: 'Koch' },
  { value: 'regiomontanus', label: 'Regiomontanus' },
  { value: 'campanus', label: 'Campanus' },
  { value: 'topocentric', label: 'Topocentric' },
] as const;

export type HouseSystem = typeof HOUSE_SYSTEMS[number]['value'];

export function isHouseSystem(value: string): value is HouseSystem {
  return HOUSE_SYSTEMS.some((s) => s.value === value);
}

export function getCusps(system: HouseSystem, JD: number, lat: number, lon: number): number[] {
  switch (system) {
    case 'whole': return wholeSignCusps(JD, lat, lon);
    case 'equal': return equalCusps(JD, lat, lon);
    case 'porphyry': return porphyryCusps(JD, lat, lon);
    case 'koch': return kochCusps(JD, lat, lon);
    case 'regiomontanus': return positionCircleCusps('regiomontanus', JD, lat, lon);
    case 'campanus': return positionCircleCusps('campanus', JD, lat, lon);
    case 'topocentric': return topocentricCusps(JD, lat, lon);
    case 'placidus':
    default: return placidusCusps(JD, lat, lon);
  }
}

export function houseOf(longitude: number, cusps: number[]): number {
  // Rotate everything so the Ascendant (cusp 1) sits at 0 and we can
  // compare against monotonically increasing arc lengths. This avoids
  // wraparound bugs (e.g. Capricorn placements with a Leo Ascendant
  // collapsing into the same house because cusp[2] < cusp[1] numerically).
  if (!Array.isArray(cusps) || cusps.length < 13 || typeof cusps[1] !== 'number') {
    return 1;
  }
  const asc = cusps[1];
  const arc = (lon: number) => ((lon - asc) % 360 + 360) % 360;
  const lonArc = arc(longitude);
  for (let h = 1; h <= 12; h++) {
    const startArc = arc(cusps[h]);
    const nextCusp = cusps[h === 12 ? 1 : h + 1];
    let endArc = arc(nextCusp);
    if (endArc <= startArc) endArc += 360;
    const test = lonArc < startArc ? lonArc + 360 : lonArc;
    if (test >= startArc && test < endArc) return h;
  }
  return 1;
}
